import { GridOptions, RowNode, SelectionChangedEvent } from 'ag-grid-community';
import { difference } from 'lodash';
import React, {
  ComponentType,
  FC,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import { useOnGridReady } from './use-on-grid-ready';

type KnownProps = Pick<
  GridOptions,
  | 'isRowSelectable'
  | 'onSelectionChanged'
  | 'rowSelection'
  | 'suppressRowClickSelection'
  | 'suppressRowDeselection'
>;

export type IsSelectable = (id: string) => boolean;
type SelectionChangeHandler = (selections: string[]) => void;

export type SelectionsProps = {
  multiSelect: boolean;
  isSelectable?: IsSelectable;
  selected: string[];
  onSelectionChange: SelectionChangeHandler;
  selectableRef?: RefObject<Selectable>;
};

export interface Selectable {
  resetSelections: () => void;
  selectAll: () => void;
}

export const withSelections = <
  T extends KnownProps & Pick<GridOptions, 'onGridReady' | 'columnDefs'>,
>(
  Component: ComponentType<T>,
): FC<Omit<T, keyof KnownProps> & SelectionsProps> => {
  const Next: FC<Omit<T, keyof KnownProps> & SelectionsProps> = ({
    onGridReady: onReady,
    multiSelect,
    isSelectable,
    selected,
    onSelectionChange: onSelected,
    columnDefs,
    selectableRef,
    ...props
  }) => {
    const isSelectableRef = useRef<IsSelectable | null>(isSelectable ?? null);
    const onSelectedRef = useRef<SelectionChangeHandler | null>(
      onSelected ?? null,
    );
    const isRowSelectable = useCallback(
      (node: RowNode) => isSelectableRef.current?.(node.id),
      [isSelectableRef],
    );
    const onSelectionChanged = useCallback(
      ({ api }: SelectionChangedEvent) =>
        onSelectedRef.current?.(api.getSelectedNodes().map((x) => x.id)),
      [onSelectedRef],
    );
    const [apiRef, , onGridReady] = useOnGridReady(onReady);
    const selectedRef = useRef<string[] | null>(null);

    useEffect(() => {
      isSelectableRef.current = isSelectable ?? null;
    }, [isSelectableRef, isSelectable]);

    useEffect(() => {
      onSelectedRef.current = onSelected ?? null;
    }, [onSelectedRef, onSelected]);

    useImperativeHandle(
      selectableRef,
      () => ({
        resetSelections: () => apiRef.current?.deselectAll?.(),
        selectAll: () => apiRef.current?.selectAllFiltered?.(),
      }),
      [apiRef],
    );

    useEffect(() => {
      if (apiRef.current == null) {
        selectedRef.current = selected ?? null;

        return;
      }
      const previous_selected = selectedRef.current ?? [];
      const current_selected = selected ?? [];
      const removed = difference(previous_selected, current_selected);
      const added = difference(selected, previous_selected);
      const get = apiRef.current.getRowNode.bind(apiRef.current);

      added.forEach((id) => get(id)?.setSelected(true, !multiSelect, true));
      removed.forEach((id) => get(id)?.setSelected(false, !multiSelect, true));
      selectedRef.current = selected ?? null;
    }, [selected, selectedRef, multiSelect, apiRef]);

    return (
      <Component
        {...(props as any)}
        onGridReady={onGridReady}
        isRowSelectable={isRowSelectable}
        onSelectionChanged={onSelectionChanged}
        rowSelection={multiSelect ? 'multiple' : 'single'}
        suppressRowClickSelection
        suppressRowDeselection
        columnDefs={
          (columnDefs ?? []).length > 0 && multiSelect
            ? [
                {
                  field: 'checkbox',
                  headerName: '',
                  pinned: 'left',
                  lockPinned: true,
                  lockPosition: true,
                  initialWidth: 40,
                  resizable: false,
                  sortable: false,
                  editable: false,
                  checkboxSelection: multiSelect,
                  cellStyle: { borderColor: 'transparent !important' },
                },
                ...(columnDefs ?? []),
              ]
            : columnDefs
        }
      />
    );
  };

  return setDisplayName(wrapDisplayName(Component, 'WithSelections'))(Next);
};
